/**
 * The "what did they win" preview, shared by the Reward Delivery queue and the
 * Initial Rewards ladder.
 *
 * Deliberately generic: it takes a title, a detail bag and an optional list of
 * label/value facts, rather than a fulfillment row. The two callers describe very
 * different things — one a won prize with a winner and a date, the other a reward
 * definition with no claimant — so the facts are supplied by the caller.
 */

// 'Baikal Book' ships only a normal weight, so emphasis has to come from the
// Medium/Bold families rather than a numeric font-weight.
const FONT_BOOK = '"Baikal Book", sans-serif';
const FONT_MEDIUM = '"Baikal Medium", sans-serif';
const FONT_BOLD = '"Baikal Bold", sans-serif';

const overlay = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  zIndex: 1000,
};

const box = {
  backgroundColor: 'white',
  borderRadius: '12px',
  maxWidth: '460px',
  width: '100%',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)',
};

export function resolveRewardImageUrl(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith('s3://')) return imagePath.replace('s3://', 'https://');
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  return imagePath.includes('/') ? imagePath : null;
}

const RewardPreviewModal = ({ title, detail = {}, facts = [], onClose }) => {
  const imageUrl = resolveRewardImageUrl(detail.primary_image_url);
  const shownFacts = facts.filter(([, value]) => value != null && value !== '');

  return (
    <div
      style={overlay}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={box} role="dialog" aria-modal="true" aria-label="Reward details">
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '24px 24px 16px',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '20px',
              color: '#111827',
              fontFamily: FONT_BOLD,
              lineHeight: 1.25,
            }}
          >
            {detail.title || title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              border: 'none',
              background: 'transparent',
              fontSize: '22px',
              lineHeight: 1,
              color: '#6b7280',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '0 24px 24px' }}>
          {imageUrl && (
            // auto width/height inside max bounds, so the element hugs the artwork.
            // A fixed width with objectFit:contain letterboxes it and the fill shows
            // as a grey band around the image.
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
              <img
                src={imageUrl}
                alt=""
                style={{
                  maxWidth: '100%',
                  maxHeight: 220,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 8,
                  border: '1px solid #e9ecef',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {detail.description && (
            <p
              style={{
                margin: '0 0 20px',
                fontSize: '14px',
                lineHeight: 1.6,
                color: '#374151',
                fontFamily: FONT_BOOK,
                whiteSpace: 'pre-wrap',
              }}
            >
              {detail.description}
            </p>
          )}

          {shownFacts.map(([label, value]) => (
            <div
              key={label}
              style={{
                display: 'grid',
                gridTemplateColumns: '104px 1fr',
                gap: '16px',
                padding: '11px 0',
                borderTop: '1px solid #f3f4f6',
                fontSize: '14px',
                fontFamily: FONT_BOOK,
              }}
            >
              <span
                style={{
                  color: '#6b7280',
                  fontSize: '11px',
                  fontFamily: FONT_MEDIUM,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {label}
              </span>
              <span style={{ color: '#111827' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RewardPreviewModal;
